import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { jsonFetch } from '@/utils/jsonFetch'
import { useParentPortalData } from './ParentHome'

type DailyUpdateKind = 'before_school' | 'after_school' | 'general'

type StudentDailyUpdate = {
  id: string | null
  studentId: string | null
  guardianUserId: string | null
  kind: DailyUpdateKind | null
  mood: string | null
  note: string | null
  createdAt: string | null
}

const KIND_OPTIONS: { value: DailyUpdateKind; label: string }[] = [
  { value: 'before_school', label: 'Before school' },
  { value: 'after_school', label: 'After school' },
  { value: 'general', label: 'General' },
]

const MOOD_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select a mood (optional)' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'tired', label: 'Tired' },
  { value: 'focused', label: 'Focused' },
  { value: 'anxious', label: 'Anxious' },
  { value: 'excited', label: 'Excited' },
]

const HISTORY_LIMIT = 20

function formatDate(value?: string | null) {
  if (!value) return ''
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function kindLabel(kind: DailyUpdateKind | null) {
  const match = KIND_OPTIONS.find((opt) => opt.value === kind)
  return match?.label || 'Update'
}

export default function ParentStudentDetail() {
  const ctx = useParentPortalData()
  const data = ctx?.data ?? null
  const { studentId } = useParams<{ studentId: string }>()
  const navigate = useNavigate()
  const mounted = useRef(true)

  const student = useMemo(
    () => data?.students?.find((entry: { id: string }) => entry.id === studentId) || null,
    [data?.students, studentId],
  )

  const [kind, setKind] = useState<DailyUpdateKind>('before_school')
  const [mood, setMood] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [updates, setUpdates] = useState<StudentDailyUpdate[]>([])
  const [updatesError, setUpdatesError] = useState<string | null>(null)
  const [loadingUpdates, setLoadingUpdates] = useState(true)

  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  const loadUpdates = useCallback(async () => {
    if (!studentId) return

    setLoadingUpdates(true)
    setUpdatesError(null)

    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
      const payload = await jsonFetch<{ updates: StudentDailyUpdate[] }>(
        `${CORE_API}/api/v1/parent/daily-update?studentId=${studentId}&limit=${HISTORY_LIMIT}`,
      )
      if (!mounted.current) return
      setUpdates(Array.isArray(payload.updates) ? payload.updates : [])
    } catch (err) {
      if (!mounted.current) return
      console.warn('[ParentStudentDetail] failed to load daily updates', err)
      setUpdatesError(
        err instanceof Error ? err.message : 'Unable to load daily updates for this student right now.',
      )
      setUpdates([])
    } finally {
      if (!mounted.current) return
      setLoadingUpdates(false)
    }
  }, [studentId])

  useEffect(() => {
    if (!studentId) {
      setLoadingUpdates(false)
      return
    }
    loadUpdates()
  }, [loadUpdates, studentId])

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (!studentId) {
        setFormError('Student not found.')
        return
      }

      const trimmed = note.trim()
      if (!trimmed) {
        setFormError('Please add a short note for the teacher.')
        return
      }

      setFormError(null)
      setSubmitting(true)

      try {
        const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
        await jsonFetch(`${CORE_API}/api/v1/parent/daily-update`, {
          method: 'POST',
          body: JSON.stringify({
            studentId,
            kind,
            mood: mood || null,
            note: trimmed,
          }),
        })

        if (!mounted.current) return
        setNote('')
        setMood('')
        setKind('before_school')
        await loadUpdates()
      } catch (err) {
        if (!mounted.current) return
        console.warn('[ParentStudentDetail] failed to submit daily update', err)
        setFormError(err instanceof Error ? err.message : 'Unable to save your update right now.')
      } finally {
        if (!mounted.current) return
        setSubmitting(false)
      }
    },
    [kind, loadUpdates, mood, note, studentId],
  )

  if (!studentId || !student) {
    return (
      <div className="PortalShell">
        <header className="PortalHeader">
          <button onClick={() => navigate(-1)} className="BackLink">
            ← Back
          </button>
          <h1>Student overview</h1>
          <p>We couldn’t find that student on your account.</p>
        </header>

        <section className="Card">
          <p className="text-sm text-cocoa/70">
            Please return to your student list to select an available profile.
          </p>
          <Link to="/portal/parent" className="Button mt-4 inline-flex w-fit">
            Back to students
          </Link>
        </section>
      </div>
    )
  }

  return (
    <div className="PortalShell">
      <header className="PortalHeader">
        <Link to="/portal/parent" className="BackLink">
          ← Back to students
        </Link>
        <div className="flex items-center justify-between">
          <h1>{student.displayName || student.fullName || 'Student overview'}</h1>
          <Link
            to={`/portal/parent/students/${studentId}/profile`}
            className="rounded-xl bg-brick px-4 py-2 text-sm font-semibold text-white hover:bg-brickDark"
          >
            Edit Profile
          </Link>
        </div>
        <p>Share quick context with teachers before or after the school day.</p>
      </header>

      <section className="Card space-y-5">
        <div>
          <h2 className="Title">Today’s update</h2>
          <p className="text-sm text-cocoa/70">Send a short note to your student’s teachers.</p>
        </div>

        {formError ? <p className="text-sm text-rose-600">{formError}</p> : null}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-cocoa">When is this for?</p>
            <div className="flex flex-wrap gap-3">
              {KIND_OPTIONS.map((option) => (
                <label key={option.value} className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-sand px-3 py-2 text-sm text-cocoa hover:border-cocoa/60">
                  <input
                    type="radio"
                    name="kind"
                    value={option.value}
                    checked={kind === option.value}
                    onChange={() => setKind(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-cocoa" htmlFor="mood">
              Mood (optional)
            </label>
            <select
              id="mood"
              name="mood"
              value={mood}
              onChange={(event) => setMood(event.target.value)}
              className="Input w-full"
            >
              {MOOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-cocoa" htmlFor="note">
              Note
            </label>
            <textarea
              id="note"
              name="note"
              required
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="1–2 sentences to help set your student up for success today."
              className="Input min-h-[96px] w-full"
            />
            <p className="text-xs text-cocoa/60">Visible to your student’s teachers.</p>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button type="submit" className="Button" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send update'}
            </button>
          </div>
        </form>
      </section>

      <section className="Card space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="Title">Recent updates</h2>
            <p className="text-sm text-cocoa/70">Last {HISTORY_LIMIT} notes you’ve shared.</p>
          </div>
          <button className="Button ghost small" onClick={loadUpdates} disabled={loadingUpdates}>
            {loadingUpdates ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loadingUpdates ? (
          <p className="text-sm text-cocoa/70">Loading updates…</p>
        ) : updatesError ? (
          <p className="text-sm text-rose-600">{updatesError}</p>
        ) : updates.length === 0 ? (
          <p className="text-sm text-cocoa/70">
            No updates yet. Share a quick note to let teachers know how the day is going.
          </p>
        ) : (
          <ul className="space-y-3">
            {updates.map((update, index) => (
              <li
                key={update.id ?? `${update.createdAt ?? 'update'}-${index}`}
                className="CardRow items-start"
              >
                <div className="flex-1 space-y-2">
                  <p className="Title text-base font-semibold">{kindLabel(update.kind)}</p>
                  <p className="whitespace-pre-wrap text-sm text-cocoa/80">{update.note}</p>
                  <p className="text-xs text-cocoa/60">
                    {update.mood ? `${update.mood} • ` : ''}
                    {formatDate(update.createdAt)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
